import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import useMutate from "../../../../../../hooks/useMutate";
import { useCustomSearchParams } from "../../../../../../hooks/useCustomSearchParams";
import { updateCardCustomField } from "../../../../../../services/apiCards";
import {
  getCustomFieldInvalidateKeys,
  getInitialCustomFieldState,
  getNextCheckboxValue,
  getParsedChoiceValues,
  hasNumberValueChanged,
  hasTextValueChanged,
} from "../utils/customFieldUtils";

export function useCustomFieldState({ field, cardId, boardId }) {
  const queryClient = useQueryClient();
  const [milestone] = useCustomSearchParams("milestone");
  const { value } = field || {};
  const initialState = useMemo(
    () => getInitialCustomFieldState(value),
    [value]
  );
  const [textValue, setTextValue] = useState(initialState.textValue);
  const [numberValue, setNumberValue] = useState(initialState.numberValue);
  const [lastApiValue, setLastApiValue] = useState(initialState.lastApiValue);
  const [choiceValue, setChoiceValue] = useState(initialState.choiceValue);
  const enterPressedRef = useRef(false);
  const numberEnterPressedRef = useRef(false);
  const previousChoiceValueRef = useRef(initialState.choiceValue);

  useEffect(() => {
    const nextState = getInitialCustomFieldState(value);
    setTextValue(nextState.textValue);
    setNumberValue(nextState.numberValue);
    setLastApiValue(nextState.lastApiValue);
    setChoiceValue(nextState.choiceValue);
    previousChoiceValueRef.current = nextState.choiceValue;
  }, [value]);

  const invalidateKeys = useMemo(
    () => getCustomFieldInvalidateKeys(cardId, boardId),
    [boardId, cardId]
  );

  const apiFn = useCallback(
    ({ cardId: targetCardId, data }) => updateCardCustomField(targetCardId, data),
    []
  );

  const { mutate: updateCardmutate, isPending } = useMutate(
    apiFn,
    invalidateKeys,
    () => {},
    () => {
      if (boardId || boardId === 0) {
        queryClient.invalidateQueries({
          queryKey: ["feeds", Number(boardId), milestone],
        });
      }
    },
    () => {
      setChoiceValue(previousChoiceValueRef.current || "");
    }
  );

  const selectedCheckboxValues = useMemo(
    () => getParsedChoiceValues(choiceValue),
    [choiceValue]
  );

  const handleFieldChange = useCallback(
    (newValue, extraData = {}) => {
      previousChoiceValueRef.current = extraData.previousValue || "";
      updateCardmutate({
        cardId,
        data: {
          column_key: field?.column_key,
          value: newValue,
        },
      });
    },
    [cardId, field?.column_key, updateCardmutate]
  );

  const handleTextChange = useCallback((event) => {
    setTextValue(event.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && hasTextValueChanged(textValue, lastApiValue)) {
        enterPressedRef.current = true;
        handleFieldChange(textValue);
        setLastApiValue(textValue);
        event.target.blur();
      }
    },
    [handleFieldChange, lastApiValue, textValue]
  );

  const handleBlur = useCallback(() => {
    if (!enterPressedRef.current && hasTextValueChanged(textValue, lastApiValue)) {
      handleFieldChange(textValue);
      setLastApiValue(textValue);
    }

    enterPressedRef.current = false;
  }, [handleFieldChange, lastApiValue, textValue]);

  const handleSelectChange = useCallback(
    (event) => {
      handleFieldChange(event.target.value);
    },
    [handleFieldChange]
  );

  const handleRadioChange = useCallback(
    (event) => {
      const previousValue = choiceValue || "";
      const newValue = event.target.value === choiceValue ? "" : event.target.value;

      setChoiceValue(newValue);
      handleFieldChange(newValue, { previousValue });
    },
    [choiceValue, handleFieldChange]
  );

  const handleRadioClick = useCallback(
    (option) => {
      if (option === choiceValue) {
        const previousValue = choiceValue || "";
        setChoiceValue("");
        handleFieldChange("", { previousValue });
      }
    },
    [choiceValue, handleFieldChange]
  );

  const handleCheckboxChange = useCallback(
    (event) => {
      const previousValue = choiceValue || "";
      const newValue = getNextCheckboxValue(
        choiceValue,
        event.target.name,
        event.target.checked
      );

      setChoiceValue(newValue);
      handleFieldChange(newValue, { previousValue });
    },
    [choiceValue, handleFieldChange]
  );

  const handleDateChange = useCallback(
    (newDate) => {
      if (newDate !== lastApiValue) {
        handleFieldChange(newDate);
        setLastApiValue(newDate);
      }
    },
    [handleFieldChange, lastApiValue]
  );

  const handleNumberChange = useCallback((event) => {
    setNumberValue(event.target.value);
  }, []);

  const handleNumberKeyDown = useCallback(
    (event) => {
      if (
        event.key === "Enter" &&
        hasNumberValueChanged(numberValue, lastApiValue)
      ) {
        numberEnterPressedRef.current = true;
        handleFieldChange(Number(numberValue));
        setLastApiValue(numberValue);
        event.target.blur();
      }
    },
    [handleFieldChange, lastApiValue, numberValue]
  );

  const handleNumberBlur = useCallback(() => {
    if (
      !numberEnterPressedRef.current &&
      hasNumberValueChanged(numberValue, lastApiValue)
    ) {
      handleFieldChange(Number(numberValue));
      setLastApiValue(numberValue);
    }

    numberEnterPressedRef.current = false;
  }, [handleFieldChange, lastApiValue, numberValue]);

  return {
    choiceValue,
    handleBlur,
    handleCheckboxChange,
    handleDateChange,
    handleKeyDown,
    handleNumberBlur,
    handleNumberChange,
    handleNumberKeyDown,
    handleRadioChange,
    handleRadioClick,
    handleSelectChange,
    handleTextChange,
    isPending,
    numberValue,
    selectedCheckboxValues,
    textValue,
    value,
  };
}
